import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-jalaali';
import { Button } from 'components/elements';
import AddReminder from 'components/addReminder';
import RemoveReminderList from 'components/removeReminderList';
import EventCard from 'components/eventCard';
import { zDateDetails, zStartEndOfMonth, zMonth, zYear, EventType } from '@polgozar/utils';
import { jalaliMonth } from 'utils/jalaliMonth';
import { zreYearFromGregorian } from 'utils/zreYearFromGregorian';
import { Row, Col, Popconfirm, message, Select } from 'antd';
import { BellOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  PageHeader,
  GroupSelectWrapper,
  ViewSwitch,
  ViewSwitchButton,
  AnimatedCalendarView,
  LegendIcon,
  StyledWrapper,
  WeekDayWrapper,
  WeekdayLabelLong,
  WeekdayLabelShort,
  DayWrapper,
  WeekRowWrapper,
  CalendarWrapper,
  PersonalEventFormSection,
  PersonalEventFormSectionTitle,
  HeaderNavWrapper,
  HeaderNavTitle,
  HeaderNavAction,
  WeekHeaderWrapper,
  CalendarDetailsWrapper,
  DetailsCard,
  PersonalEventsCard,
  PersonalEventsList,
  PersonalEventItem,
  PersonalEventColor,
  PersonalEventBody,
  PersonalEventSubject,
  PersonalEventDates,
  PersonalEventActions,
  PersonalEventEditButton,
  PersonalEventDeleteButton,
  PersonalEventsEmpty,
  ReminderCard,
  ReminderHint,
  DetailsHeader,
  DetailsContainer,
  LegendWrapper,
  LegendRow,
  PartyDay,
  NaborDay,
  PersonalEventDay,
  DayIndicators,
  EventBar,
  ZEvent,
  ZDetails,
  EventDay,
  DayCellInfoStack,
  DayCellGregorian,
  DayCellMagian,
  DayCellFasliName,
  FasliMonthTitle,
  KhayyamMonthTitle,
  GregorianMonthSubtitle,
  InnerContent,
  EventSpaceBar,
  EventWrapper,
  EventTitle,
} from './index.styled';
import {
  namedOperations,
  useDeletePersonalEventMutation,
  useGetMyAccountQuery,
  useGetMyPersonalEventsQuery,
  useGetAllEventsLazyQuery,
  useImportGoogleCalendarMutation,
} from 'graphql/types';
import { requestGoogleAuthCode, GOOGLE_CALENDAR_READONLY_SCOPE } from 'helpers/googleIdentity';
import type { PersonalEventFieldsFragment } from 'graphql/types';
import PersonalEventFormPanel from 'containers/personalEventCreate/PersonalEventFormPanel';
import {
  dayMatchesPersonalEventDate,
  getPersonalEventDates,
} from 'containers/personalEventCreate/personalEventFormUtils';

const today = moment();

function formatPersonalEventDate(date: number) {
  const s = String(date);
  if (s.length === 8) {
    return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}`;
  }
  return `Day ${date}`;
}

const isStartOnMonday = true;
const weekTypeFormat = isStartOnMonday ? 'isoWeek' : 'week';

function getWeekDayLabels(isMobile: boolean): string[] {
  let days = isMobile ? [...moment.weekdaysShort()] : [...moment.weekdays()];
  if (isStartOnMonday) {
    days = [...days.slice(1), days[0]];
  }
  return days;
}

type PersonalEventFormState = { mode: 'create' } | { mode: 'edit'; id: string };

const Calendar = () => {
  const weekDaysLong = useMemo(() => getWeekDayLabels(false), []);
  const weekDaysShort = useMemo(() => getWeekDayLabels(true), []);

  const { data: accountResult } = useGetMyAccountQuery();
  const { data: personalEventsResult, loading: personalEventsLoading } = useGetMyPersonalEventsQuery();

  const personalEvents = useMemo(
    () => (personalEventsResult?.myPersonalEvents ?? []).filter((event): event is PersonalEventFieldsFragment => !!event),
    [personalEventsResult],
  );

  const [deletePersonalEvent, { loading: deletingPersonalEvent }] = useDeletePersonalEventMutation({
    refetchQueries: [namedOperations.Query.GetMyPersonalEvents],
  });

  const [importGoogleCalendar, { loading: importingGoogleCalendar }] = useImportGoogleCalendarMutation({
    refetchQueries: [namedOperations.Query.GetMyPersonalEvents],
  });

  const handleImportGoogleCalendar = useCallback(async () => {
    try {
      const code = await requestGoogleAuthCode(GOOGLE_CALENDAR_READONLY_SCOPE);
      const result = await importGoogleCalendar({ variables: { input: { code } } });
      const imported = result.data?.importGoogleCalendar?.imported ?? 0;
      message.success(
        result.data?.importGoogleCalendar?.message ?? `Imported ${imported} event${imported === 1 ? '' : 's'} from Google Calendar.`,
      );
    } catch (err) {
      if (err instanceof Error && err.message !== 'access_denied') {
        message.error(err.message || 'Failed to import from Google Calendar.');
      }
    }
  }, [importGoogleCalendar]);

  const handleDeletePersonalEvent = useCallback(
    async (personalEventId: string) => {
      try {
        await deletePersonalEvent({ variables: { personalEventId } });
        message.success('Personal event removed.');
      } catch (err) {
        if (err instanceof Error) {
          message.error(err.message);
        }
      }
    },
    [deletePersonalEvent],
  );

  const getPersonalEventsForDay = useCallback(
    (day: moment.Moment) =>
      personalEvents.filter((event) =>
        getPersonalEventDates(event).some((dateKey) => dayMatchesPersonalEventDate(day, dateKey)),
      ),
    [personalEvents],
  );

  const [calendarView, setCalendarView] = useState<'fasli' | 'magian'>('fasli');
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(moment());
  const [personalEventForm, setPersonalEventForm] = useState<PersonalEventFormState | null>(null);
  const [draftPersonalEventDates, setDraftPersonalEventDates] = useState<number[]>([]);
  const history = useHistory();
  const location = useLocation();
  const personalEventFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('personalEvent');
    const id = params.get('id');
    if (mode === 'create') {
      setPersonalEventForm({ mode: 'create' });
    } else if (mode === 'edit' && id) {
      setPersonalEventForm({ mode: 'edit', id });
    }
  }, [location.search]);

  useEffect(() => {
    if (!personalEventForm || !personalEventFormRef.current) return;
    personalEventFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [personalEventForm]);

  const closePersonalEventForm = useCallback(() => {
    setPersonalEventForm(null);
    setDraftPersonalEventDates([]);
    if (location.search) {
      history.replace('/calendar');
    }
  }, [history, location.search]);

  const openCreatePersonalEventForm = useCallback(() => {
    setPersonalEventForm({ mode: 'create' });
    setDraftPersonalEventDates([]);
  }, []);

  const openEditPersonalEventForm = useCallback((id: string) => {
    setPersonalEventForm({ mode: 'edit', id });
    setDraftPersonalEventDates([]);
  }, []);

  const findUserBooking = useCallback(
    (date: moment.Moment) => {
      const bookings = accountResult?.myAccount?.data?.attributes?.bookings?.data;
      if (!bookings || !bookings.length) {
        return [];
      }
      return bookings
        .filter((booking) => {
          if (!booking.attributes.event.data) return false;

          const { startTime, endTime } = booking.attributes.event.data.attributes;
          return date.isSameOrAfter(moment(startTime), 'day') && date.isSameOrBefore(moment(endTime), 'day');
        })
        .map((booking) => booking.attributes.event.data);
    },
    [accountResult],
  );

  // Group filter: pick one or more of the user's created/joined groups to view their events on the calendar.
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const ledGroups = accountResult?.myAccount?.data?.attributes?.ledGroups?.data ?? [];
  const joinedGroups = useMemo(() => {
    const ledGroupIds = new Set(ledGroups.map((group) => group.id));
    const groups = accountResult?.myAccount?.data?.attributes?.groups?.data ?? [];
    return groups.filter((group) => !ledGroupIds.has(group.id));
  }, [accountResult, ledGroups]);

  const [getGroupEvents, { data: groupEventsResult, loading: groupEventsLoading }] = useGetAllEventsLazyQuery();

  useEffect(() => {
    if (!selectedGroupIds.length) {
      return;
    }
    getGroupEvents({
      variables: {
        pagination: { limit: 100 },
        sort: 'startTime:asc',
        filters: { group: { id: { in: selectedGroupIds } } },
      },
    });
  }, [selectedGroupIds, getGroupEvents]);

  const groupEvents = useMemo(() => groupEventsResult?.events?.data ?? [], [groupEventsResult]);

  const findGroupEventsForDay = useCallback(
    (date: moment.Moment) =>
      groupEvents.filter((event) => {
        const { startTime, endTime } = event.attributes ?? {};
        if (!startTime) return false;
        return date.isSameOrAfter(moment(startTime), 'day') && date.isSameOrBefore(moment(endTime ?? startTime), 'day');
      }),
    [groupEvents],
  );

  // Active event source for the calendar: the selected groups' events, or the user's booked events.
  const getEventsForDay = useCallback(
    (date: moment.Moment) => (selectedGroupIds.length ? findGroupEventsForDay(date) : findUserBooking(date)),
    [selectedGroupIds, findGroupEventsForDay, findUserBooking],
  );

  const selectedDateUserEvents = useMemo(() => {
    const events = getEventsForDay(selectedDate);
    if (!events || !events.length) {
      return null;
    }
    return (
      <EventWrapper>
        <EventTitle>{selectedGroupIds.length ? 'Group Events' : 'Your Scheduled Event'}</EventTitle>
        {events.map((event) => (
          <EventCard key={event.id} {...event.attributes} />
        ))}
      </EventWrapper>
    );
  }, [getEventsForDay, selectedDate, selectedGroupIds]);

  const headerGregorianMonthNames = useMemo(() => {
    if (calendarView === 'fasli') {
      const startEndMonth = zStartEndOfMonth(currentMonth.clone());
      if (startEndMonth[0].format('MMM') !== startEndMonth[1].format('MMM')) {
        return [startEndMonth[0].format('MMMM'), startEndMonth[1].format('MMMM')];
      }
      return [startEndMonth[0].format('MMMM')];
    }
    const start = currentMonth.clone().startOf('jMonth');
    const end = currentMonth.clone().endOf('jMonth');
    if (start.format('MMM') !== end.format('MMM')) {
      return [start.format('MMMM'), end.format('MMMM')];
    }
    return [start.format('MMMM')];
  }, [calendarView, currentMonth]);

  const headerGregorianYearLabel = useMemo(() => {
    if (calendarView === 'fasli') {
      const se = zStartEndOfMonth(currentMonth.clone());
      if (se[0].year() !== se[1].year()) {
        return `${se[0].format('YYYY')} – ${se[1].format('YYYY')}`;
      }
      return se[0].format('YYYY');
    }
    const start = currentMonth.clone().startOf('jMonth');
    const end = currentMonth.clone().endOf('jMonth');
    if (start.year() !== end.year()) {
      return `${start.format('YYYY')} – ${end.format('YYYY')}`;
    }
    return start.format('YYYY');
  }, [calendarView, currentMonth]);

  const renderDays = () => {
    const days = weekDaysLong.map((weekName, index) => (
      <WeekDayWrapper key={weekName}>
        <WeekdayLabelLong>{weekName}</WeekdayLabelLong>
        <WeekdayLabelShort>{weekDaysShort[index]}</WeekdayLabelShort>
      </WeekDayWrapper>
    ));

    return <WeekHeaderWrapper>{days}</WeekHeaderWrapper>;
  };

  const renderCalendarCells = (viewType: 'fasli' | 'magian') => {
    let startDate, endDate, monthStart, monthEnd;

    if (viewType === 'fasli') {
      const startEndMonth = zStartEndOfMonth(currentMonth.clone());
      monthStart = startEndMonth[0];
      monthEnd = startEndMonth[1];
      startDate = monthStart.clone().startOf(weekTypeFormat);
      endDate = monthEnd.clone().endOf(weekTypeFormat);
    } else {
      monthStart = currentMonth.clone().startOf('jMonth');
      monthEnd = currentMonth.clone().endOf('jMonth');
      startDate = monthStart.clone().startOf(weekTypeFormat);
      endDate = monthEnd.clone().endOf(weekTypeFormat);
    }

    const rows = [];
    let days = [];
    let day = startDate.clone();
    let globalSpacer: number[] = [];
    let rowIndex = 0;

    while (day <= endDate) {
      for (let i = 0; i < 7; i += 1) {
        const clonedDay = day.clone();
        const disabled = viewType === 'fasli'
          ? zMonth(day).month !== zMonth(monthStart).month
          : day.jMonth() !== monthStart.jMonth();

        const events = getEventsForDay(day);
        const zData = zDateDetails(day, weekTypeFormat);

        let preSpacer: JSX.Element[] = [];
        let spaceCount = 0;
        if (viewType === 'fasli' && zData) {
          const spacer = zData.events.filter((item) => !!item.duration).map((item) => item.duration);
          const globalSpacerCount = globalSpacer.length;
          globalSpacer = [...globalSpacer, ...spacer];

          spaceCount = globalSpacer.length || 0;
          if (globalSpacerCount) {
            preSpacer = [...Array(globalSpacerCount)].map((_, index) => (
              <EventSpaceBar key={`pre-spacer-${clonedDay.format('YYYY-MM-DD')}-${index}`} />
            ));
            spaceCount -= globalSpacerCount;
          }
          globalSpacer = globalSpacer.map((space) => space - 1).filter((item) => !!item);
        }

        const fasliDayName = zData?.name.en ?? '';
        const isToday = today.isSame(day, 'day');
        const personalEventsOnDay = getPersonalEventsForDay(day);
        const personalEventsToShow =
          personalEventsOnDay.length > 1 ? personalEventsOnDay.slice(0, 1) : personalEventsOnDay;
        const isDraftPersonalEventDate =
          Boolean(personalEventForm) &&
          draftPersonalEventDates.some((dateKey) => dayMatchesPersonalEventDate(clonedDay, dateKey));

        days.push(
          <DayWrapper
            disabled={disabled}
            selected={selectedDate.isSame(day, 'day')}
            draftDate={isDraftPersonalEventDate}
            isToday={isToday}
            key={`${viewType}-${clonedDay.format('YYYY-MM-DD')}`}
            onClick={() => onDateClick(clonedDay)}
            labelColor={viewType === 'fasli' && zData?.events?.length ? zData.events[0]?.labelColor : undefined}
            bgImage={viewType === 'fasli' && zData?.events?.length ? zData.events[0]?.bgImage : undefined}
          >
            <InnerContent>
              <DayCellInfoStack>
                <DayCellGregorian disabled={disabled}>{day.format('D')}</DayCellGregorian>
                {viewType === 'fasli' ? (
                  <>
                    <DayCellMagian disabled={disabled} isToday={isToday}>
                      {zData?.day != null ? String(zData.day) : ''}
                    </DayCellMagian>
                    <DayCellFasliName disabled={disabled}>{fasliDayName}</DayCellFasliName>
                  </>
                ) : (
                  <>
                    <DayCellMagian disabled={disabled} isToday={isToday}>
                      {day.format('jD')}
                    </DayCellMagian>
                    <DayCellFasliName disabled={disabled}>{fasliDayName}</DayCellFasliName>
                  </>
                )}
              </DayCellInfoStack>
              <ZDetails>
                <ZEvent disabled={disabled}>
                  {zData?.events && (
                    <>
                      {preSpacer}
                      {zData.events.map((e) => {
                        const isSpacer = spaceCount > 0;
                        spaceCount -= 1;
                        return <Event key={`${e.en}-event`} event={e} disabled={disabled} spacer={isSpacer} />;
                      })}
                    </>
                  )}
                </ZEvent>
              </ZDetails>
              <DayIndicators>
                {zData?.isNabor && (
                  <NaborDay disabled={disabled}>
                    <span role="img" aria-label="nabor image">
                      🌱
                    </span>
                  </NaborDay>
                )}
                {zData?.isParyDay && (
                  <PartyDay>
                    <span role="img" aria-label="party image">
                      ♫
                    </span>
                  </PartyDay>
                )}
                {!!events.length && (
                  <EventDay>
                    <span role="img" aria-label="event image">
                      🔥
                    </span>
                  </EventDay>
                )}
                {personalEventsToShow.map((personalEvent) => (
                  <PersonalEventDay
                    key={personalEvent.id}
                    disabled={disabled}
                    $color={personalEvent.color ?? '#1a2744'}
                  >
                    <span role="img" aria-label="personal event">
                      📌
                    </span>
                  </PersonalEventDay>
                ))}
              </DayIndicators>
            </InnerContent>
          </DayWrapper>,
        );
        day = day.add(1, 'day');
      }
      rows.push(
        <WeekRowWrapper key={`${viewType}-row-${rowIndex}`}>
          {days}
        </WeekRowWrapper>,
      );
      rowIndex += 1;
      days = [];
    }

    return <div>{rows}</div>;
  };

  const onDateClick = (day: moment.Moment) => {
    setSelectedDate(day);
  };

  const nextMonth = () => {
    if (calendarView === 'magian') {
      setCurrentMonth((prev) => prev.clone().add(1, 'jMonth').startOf('jMonth'));
      return;
    }

    const startEndMonth = zStartEndOfMonth(currentMonth.clone());
    const a = startEndMonth[1].clone().add(1, 'day');

    setCurrentMonth(a);
  };

  const prevMonth = () => {
    if (calendarView === 'magian') {
      setCurrentMonth((prev) => prev.clone().subtract(1, 'jMonth').startOf('jMonth'));
      return;
    }

    const startEndMonth = zStartEndOfMonth(currentMonth.clone());
    setCurrentMonth(startEndMonth[0].subtract(2, 'day'));
  };

  const gotToToday = () => {
    setCurrentMonth(moment());
  };

  const todayZdata = zDateDetails(selectedDate, weekTypeFormat);
  const currentZmonth = useMemo(() => zMonth(currentMonth), [currentMonth]);
  const currentJalaliMonth = useMemo(() => jalaliMonth(currentMonth), [currentMonth]);
  const selectedJalaliMonth = useMemo(() => jalaliMonth(selectedDate), [selectedDate]);
  const curentZyear = zYear(currentMonth);
  /** Zarvāni sidebar: Jalali j-day + Jalali month + ZRE year. */
  const selectedMagianDateLabel = useMemo(
    () => `${selectedDate.format('jD')} ${selectedJalaliMonth.name.en} ${zreYearFromGregorian(selectedDate)}`,
    [selectedDate, selectedJalaliMonth],
  );

  const renderHeader = () => {
    return (
      <HeaderNavWrapper>
        <HeaderNavTitle>
          {calendarView === 'fasli' ? (
            <>
              <FasliMonthTitle>
                {currentZmonth.name.en} {curentZyear}
              </FasliMonthTitle>
              <GregorianMonthSubtitle>
                {headerGregorianMonthNames.length > 1 ? (
                  <>
                    {headerGregorianMonthNames[0]} — {headerGregorianMonthNames[1]} {headerGregorianYearLabel}
                  </>
                ) : (
                  <>
                    {headerGregorianMonthNames[0]} {headerGregorianYearLabel}
                  </>
                )}
              </GregorianMonthSubtitle>
            </>
          ) : (
            <>
              <KhayyamMonthTitle>
                {currentJalaliMonth.name.en} {zreYearFromGregorian(currentMonth)}
              </KhayyamMonthTitle>
              <GregorianMonthSubtitle>
                {headerGregorianMonthNames.length > 1 ? (
                  <>
                    {headerGregorianMonthNames[0]} — {headerGregorianMonthNames[1]} {headerGregorianYearLabel}
                  </>
                ) : (
                  <>
                    {headerGregorianMonthNames[0]} {headerGregorianYearLabel}
                  </>
                )}
              </GregorianMonthSubtitle>
            </>
          )}
        </HeaderNavTitle>
        <HeaderNavAction>
          <Button onClick={gotToToday} type="default" ghost>
            Today
          </Button>
          <Button onClick={prevMonth} type="default" ghost leftIcon="icon-chevron-left" />
          <Button onClick={nextMonth} type="default" ghost leftIcon="icon-chevron-right" />
        </HeaderNavAction>
      </HeaderNavWrapper>
    );
  };

  interface EventProps {
    event: EventType;
    disabled?: boolean;
    spacer?: boolean;
  }

  const Event = ({ event, disabled = false, spacer = false }: EventProps) => {
    if (event.hideOnCalendar) {
      return null;
    }
    return (
      <>
        {event.duration ? (
          <EventBar
            span={event.duration}
            openEnded={event.openEnded}
            openStarted={event.openStarted}
            disabled={disabled}
            type={event.type}
          >
            {event.en}
          </EventBar>
        ) : (
          <div>{event.en}</div>
        )}
        {spacer && <EventSpaceBar />}
      </>
    );
  };

  return (
    <StyledWrapper>
      <PageHeader>
        <GroupSelectWrapper>
          <Select
            mode="multiple"
            allowClear
            value={selectedGroupIds}
            onChange={(value) => setSelectedGroupIds(value as string[])}
            loading={groupEventsLoading}
            placeholder="Select groups"
            maxTagCount="responsive"
            options={[
              ...(ledGroups.length
                ? [
                  {
                    label: "Groups I've created",
                    options: ledGroups.map((group) => ({
                      label: group.attributes?.name ?? 'Untitled group',
                      value: group.id,
                    })),
                  },
                ]
                : []),
              ...(joinedGroups.length
                ? [
                  {
                    label: 'Joined Groups',
                    options: joinedGroups.map((group) => ({
                      label: group.attributes?.name ?? 'Untitled group',
                      value: group.id,
                    })),
                  },
                ]
                : []),
            ]}
          />
        </GroupSelectWrapper>
        <ViewSwitch>
          <ViewSwitchButton active={calendarView === 'fasli'} onClick={() => setCalendarView('fasli')}>
            Fasli
          </ViewSwitchButton>
          <ViewSwitchButton active={calendarView === 'magian'} onClick={() => setCalendarView('magian')}>
            Zarvāni
          </ViewSwitchButton>
        </ViewSwitch>
      </PageHeader>
      <AnimatedCalendarView key={calendarView}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <CalendarWrapper>
              {renderHeader()}
              {renderDays()}
              {renderCalendarCells(calendarView)}
              {personalEventForm && (
                <PersonalEventFormSection ref={personalEventFormRef}>
                  <PersonalEventFormSectionTitle>
                    {personalEventForm.mode === 'edit' ? 'Edit Personal Event' : 'Add Personal Event'}
                  </PersonalEventFormSectionTitle>
                  <PersonalEventFormPanel
                    selectedDate={selectedDate}
                    editId={personalEventForm.mode === 'edit' ? personalEventForm.id : null}
                    onCancel={closePersonalEventForm}
                    onSaved={closePersonalEventForm}
                    onDateListChange={setDraftPersonalEventDates}
                  />
                </PersonalEventFormSection>
              )}
              {calendarView === 'fasli' && (
                <LegendWrapper>
                  <LegendRow>
                    <LegendIcon type="today" /> Today date
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="important" /> Important days
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="semiImportant" /> Semi important days
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="selected" /> User Selected Day
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="gohanbar" /> Gāhānbār days
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="ziyarat" /> Zīyārat days
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="nabor">
                      <span role="img" aria-label="nabor image">
                        🌱
                      </span>
                    </LegendIcon>
                    Nabor Days
                  </LegendRow>
                  <LegendRow>
                    <LegendIcon type="party">
                      <span role="img" aria-label="party image">
                        ♫
                      </span>
                    </LegendIcon>
                    Dei celebration Days
                  </LegendRow>
                </LegendWrapper>
              )}
            </CalendarWrapper>
          </Col>
          <Col xs={24} lg={8}>
            <CalendarDetailsWrapper>
              <DetailsCard>
                <DetailsHeader>
                  {calendarView === 'fasli'
                    ? `More about the month of ${currentZmonth.name.en} and the day of ${todayZdata.name.en}`
                    : `More about the month of ${selectedJalaliMonth.name.en} and the selected Zarvāni date`}
                </DetailsHeader>
                <DetailsContainer>
                  <ul>
                    {calendarView === 'fasli' ? (
                      <>
                        <li>{selectedDate.format('D MMMM YYYY')} in Gregorian calendar</li>
                        <li>
                          {todayZdata.day} {currentZmonth.name.en} {curentZyear} in Fasli calendar
                        </li>
                        {todayZdata.isNabor && <li>Its Nabor day</li>}
                        <li>Name of the day is {todayZdata.name.en}</li>
                        {todayZdata.events.map((event, index) => (
                          <li key={`${event.en}-${index}`}>{event.en}</li>
                        ))}
                        {!!todayZdata.name.info && <li>{todayZdata.name.info}</li>}
                      </>
                    ) : (
                      <>
                        <li>{selectedMagianDateLabel} in Zarvāni calendar</li>
                        <li>{selectedDate.format('D MMMM YYYY')} in Gregorian calendar</li>
                        <li>Day of week is {selectedDate.format('dddd')}</li>
                      </>
                    )}
                  </ul>
                </DetailsContainer>
              </DetailsCard>

              {selectedDateUserEvents}

              <PersonalEventsCard>
                <DetailsHeader>My Personal Events</DetailsHeader>

                {personalEventsLoading ? (
                  <PersonalEventsEmpty>Loading personal events...</PersonalEventsEmpty>
                ) : personalEvents.length ? (
                  <PersonalEventsList>
                    {personalEvents.map((event) => {
                      const dates = getPersonalEventDates(event);
                      return (
                        <PersonalEventItem key={event.id}>
                          <PersonalEventColor $color={event.color ?? '#1a2744'} />
                          <PersonalEventBody>
                            <PersonalEventSubject>{event.subject}</PersonalEventSubject>
                            {dates.length > 0 && (
                              <PersonalEventDates>
                                {dates.map(formatPersonalEventDate).join(', ')}
                              </PersonalEventDates>
                            )}
                          </PersonalEventBody>
                          <PersonalEventActions>
                            <PersonalEventEditButton
                              type="button"
                              aria-label={`Edit ${event.subject}`}
                              onClick={() => openEditPersonalEventForm(event.id)}
                            >
                              <EditOutlined />
                            </PersonalEventEditButton>
                            <Popconfirm
                              title="Remove this personal event?"
                              okText="Remove"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true, loading: deletingPersonalEvent }}
                              onConfirm={() => handleDeletePersonalEvent(event.id)}
                            >
                              <PersonalEventDeleteButton
                                type="button"
                                aria-label={`Remove ${event.subject}`}
                              >
                                <DeleteOutlined />
                              </PersonalEventDeleteButton>
                            </Popconfirm>
                          </PersonalEventActions>
                        </PersonalEventItem>
                      );
                    })}
                  </PersonalEventsList>
                ) : (
                  <PersonalEventsEmpty>No personal events yet.</PersonalEventsEmpty>
                )}
                {!personalEventForm && (
                  <>
                    <Button onClick={openCreatePersonalEventForm} type="dashed">
                      Add personal events to the calendar
                    </Button>
                    <Button
                      onClick={handleImportGoogleCalendar}
                      type="default"
                      loading={importingGoogleCalendar}
                    >
                      Import from Google Calendar
                    </Button>
                  </>
                )}
              </PersonalEventsCard>

              <ReminderCard>
                <DetailsHeader><BellOutlined /> Set Reminder</DetailsHeader>
                <ReminderHint>Get notified about important dates and events</ReminderHint>
                <RemoveReminderList />
                <AddReminder />
              </ReminderCard>
            </CalendarDetailsWrapper>
          </Col>
        </Row>
      </AnimatedCalendarView>
    </StyledWrapper>
  );
};

export default Calendar;
